/**
 * Creative Detail Panel
 *
 * Full-screen overlay that shows detailed creative analytics when a user
 * clicks on a creative row. Mirrors the Antares creative insight view:
 *
 *  - Top: ad group name, close button, date range
 *  - Tabs: "Ad Group" (aggregate) | "Individual Ads"
 *  - Metric cards row: ROAS, Spend, CPC, CVR, CTR, CPM
 *  - Left column: weekly performance charts
 *  - Right column: individual ad cards with thumbnails & confidence scores
 */

import { useState, useCallback } from 'react'
import * as Icons from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'
import ReactMarkdown from 'react-markdown'
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import type {
  CreativeDetailData,
  WeeklyMetricPoint,
  IndividualAd,
} from '../types'
import { agentplatService } from '@/services/agentplat'
import {
  CREATIVE_ANALYZER_AGENT_ID,
  formatCreativeAnalysisPrompt,
} from '@/config/creative-analyzer'

// ============================================================================
// Helpers
// ============================================================================

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`
  return `$${value.toFixed(2)}`
}

function formatCompact(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`
  return value.toLocaleString()
}

// ============================================================================
// Thumbnail Image (handles Meta CDN referrer policy + fallback)
// ============================================================================

function ThumbnailImage({ src, alt, className }: { src?: string; alt: string; className?: string }) {
  const [failed, setFailed] = useState(false)

  if (!src || failed) {
    return (
      <div className={cn('flex items-center justify-center bg-tf-bg-hover text-tf-text-muted', className)}>
        <Icons.Image className="w-10 h-10" />
      </div>
    )
  }

  return (
    <img
      src={src}
      alt={alt}
      className={cn('object-cover', className)}
      referrerPolicy="no-referrer"
      loading="lazy"
      onError={() => setFailed(true)}
    />
  )
}

// ============================================================================
// Metric Card
// ============================================================================

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-[11px] font-medium text-tf-text-muted uppercase tracking-wide">
        {label}
      </span>
      <span className="text-lg font-semibold text-tf-text mt-0.5">{value}</span>
    </div>
  )
}

// ============================================================================
// Chart Tooltip
// ============================================================================

interface ChartTooltipProps {
  active?: boolean
  payload?: Array<{ name: string; value: number; color: string; dataKey: string }>
  label?: string
}

function ChartTooltip({ active, payload, label }: ChartTooltipProps) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-tf-bg-card border border-tf-border rounded-lg shadow-lg p-3 text-xs">
      <p className="font-medium text-tf-text mb-1.5">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: entry.color }} />
          <span className="text-tf-text-muted">{entry.name}:</span>
          <span className="font-medium text-tf-text">
            {entry.dataKey === 'spend' || entry.dataKey === 'revenue'
              ? formatCurrency(entry.value)
              : entry.value.toFixed(2)}
          </span>
        </div>
      ))}
    </div>
  )
}

// ============================================================================
// Weekly Charts Section
// ============================================================================

function WeeklyCharts({ data }: { data: WeeklyMetricPoint[] }) {
  if (!data.length) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-tf-text-muted">
        No weekly data available
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* ROAS & Spend */}
      <div className="bg-tf-bg-card rounded-lg border border-tf-border p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-tf-text">ROAS & Spend</h4>
          <span className="text-xs text-tf-text-muted flex items-center gap-1">
            <Icons.BarChart3 className="w-3 h-3" /> Weekly
          </span>
        </div>
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis
                dataKey="week"
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                yAxisId="left"
                orientation="left"
                tickFormatter={formatCurrency}
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<ChartTooltip />} />
              <Legend iconType="square" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="left" dataKey="spend" name="Spend" fill="#99d9d9" radius={[2, 2, 0, 0]} barSize={16} />
              <Line yAxisId="right" type="monotone" dataKey="roas" name="ROAS" stroke="#3b82f6" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* CPC & CVR */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-tf-bg-card rounded-lg border border-tf-border p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-tf-text">CPC & CVR</h4>
            <span className="text-xs text-tf-text-muted flex items-center gap-1">
              <Icons.BarChart3 className="w-3 h-3" /> Weekly
            </span>
          </div>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="week" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" orientation="left" tickFormatter={(v) => `$${v.toFixed(1)}`} tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v.toFixed(1)}%`} tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Legend iconType="line" iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                <Line yAxisId="left" type="monotone" dataKey="cpc" name="CPC" stroke="#3b82f6" strokeWidth={2} dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="cvr" name="CVR" stroke="#8b5cf6" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* CTR & CPM */}
        <div className="bg-tf-bg-card rounded-lg border border-tf-border p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-tf-text">CTR & CPM</h4>
            <span className="text-xs text-tf-text-muted flex items-center gap-1">
              <Icons.BarChart3 className="w-3 h-3" /> Weekly
            </span>
          </div>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="week" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" orientation="left" tickFormatter={(v) => `${v.toFixed(1)}%`} tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `$${v.toFixed(0)}`} tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Legend iconType="line" iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                <Line yAxisId="left" type="monotone" dataKey="ctr" name="CTR" stroke="#3b82f6" strokeWidth={2} dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="cpm" name="CPM" stroke="#f59e0b" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Hook & Hold Rate (video metrics) */}
      {data.some((d) => d.hookRate != null) && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-tf-bg-card rounded-lg border border-tf-border p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-tf-text">Hook Rate</h4>
              <span className="text-xs text-tf-text-muted">3 Sec</span>
            </div>
            <div className="h-36">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="hookRate" name="3 Sec" fill="#99d9d9" radius={[2, 2, 0, 0]} barSize={14} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-tf-bg-card rounded-lg border border-tf-border p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-tf-text">Hold Rate</h4>
              <span className="text-xs text-tf-text-muted">15 Sec</span>
            </div>
            <div className="h-36">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="holdRate" name="15 Sec" fill="#c4b5fd" radius={[2, 2, 0, 0]} barSize={14} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Individual Ad Card
// ============================================================================

function AdCard({ ad }: { ad: IndividualAd }) {
  return (
    <div className="bg-tf-bg-card rounded-xl border border-tf-border overflow-hidden hover:shadow-md transition-shadow">
      {/* Thumbnail */}
      <div className="relative aspect-square bg-tf-bg-hover">
        <ThumbnailImage src={ad.thumbnail_url} alt={ad.ad_name} className="w-full h-full" />
      </div>

      {/* Info */}
      <div className="p-3">
        {/* Confidence score badge */}
        {ad.confidence && (
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: ad.confidence.color }}
              />
              <span className="text-xs font-medium text-tf-text-muted truncate max-w-[100px]">
                {ad.ad_name.length > 18
                  ? ad.ad_name.slice(0, 18) + '...'
                  : ad.ad_name}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs font-bold" style={{ color: ad.confidence.color }}>
                {ad.confidence.score.toFixed(1)}
              </span>
              <span
                className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                style={{
                  backgroundColor: ad.confidence.color + '15',
                  color: ad.confidence.color,
                }}
              >
                {ad.confidence.label}
              </span>
            </div>
          </div>
        )}

        {/* Metrics row */}
        <div className="flex items-center justify-between text-center pt-1 border-t border-tf-border-sub">
          <div>
            <p className={cn(
              'text-sm font-bold',
              ad.roas >= 1 ? 'text-tf-text' : 'text-tf-feedback-error-text'
            )}>
              {ad.roas.toFixed(2)}
            </p>
            <p className="text-[10px] text-tf-text-muted uppercase">ROAS</p>
          </div>
          <div>
            <p className="text-sm font-bold text-tf-text">{formatCompact(ad.spend)}</p>
            <p className="text-[10px] text-tf-text-muted uppercase">Spend</p>
          </div>
          <div>
            <p className="text-sm font-bold text-tf-text">{ad.lifetime_days}</p>
            <p className="text-[10px] text-tf-text-muted uppercase">Lifetime</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Ads Grid Section
// ============================================================================

type AdsTab = 'ads' | 'analysis'

function AdsGrid({ ads, detailData }: { ads: IndividualAd[]; detailData: CreativeDetailData }) {
  const [tab, setTab] = useState<AdsTab>('ads')
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysisResult, setAnalysisResult] = useState<string | null>(null)
  const [analysisError, setAnalysisError] = useState<string | null>(null)
  const [fromCache, setFromCache] = useState(false)

  const handleAnalyze = async (regenerate = false) => {
    setIsAnalyzing(true)
    setAnalysisError(null)
    if (regenerate) {
      setAnalysisResult(null)
    }
    setTab('analysis')

    try {
      const m = detailData.metrics
      const prompt = formatCreativeAnalysisPrompt({
        adSetName: detailData.ad_group_name,
        metrics: {
          spend: m?.spend || 0,
          revenue: m?.revenue || 0,
          roas: m?.roas || 0,
          ctr: m?.ctr || 0,
          cpc: m?.cpc || 0,
          cpm: m?.cpm || 0,
          impressions: m?.impressions || 0,
          clicks: m?.clicks || 0,
          conversions: m?.conversions || 0,
        },
        ads: detailData.ads.map((ad) => ({
          name: ad.ad_name,
          spend: ad.spend,
          revenue: ad.spend * ad.roas,
          roas: ad.roas,
          status: ad.status,
          days: ad.lifetime_days,
        })),
      })

      // Collect thumbnail URLs for visual analysis
      const thumbnailUrls = detailData.ads
        .map((ad) => ad.thumbnail_url)
        .filter((url): url is string => Boolean(url))
        .slice(0, 5) // Limit to 5 images

      const result = await agentplatService.invokeAgent(
        CREATIVE_ANALYZER_AGENT_ID,
        prompt,
        thumbnailUrls.length > 0 ? thumbnailUrls : undefined,
        regenerate,
      )

      setAnalysisResult(result.content)
      setFromCache(result.fromCache || false)
    } catch (error) {
      console.error('Error analyzing creatives:', error)
      setAnalysisError(error instanceof Error ? error.message : 'Failed to analyze creatives')
    } finally {
      setIsAnalyzing(false)
    }
  }

  return (
    <div>
      {/* Tab header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex border border-tf-border rounded-lg overflow-hidden">
          <button
            onClick={() => setTab('ads')}
            className={cn(
              'px-4 py-1.5 text-sm font-medium transition-colors',
              tab === 'ads' ? 'bg-tf-text text-white' : 'text-tf-text-muted hover:bg-tf-bg-hover'
            )}
          >
            Ads
          </button>
          <button
            onClick={analysisResult ? () => setTab('analysis') : () => handleAnalyze()}
            disabled={isAnalyzing}
            className={cn(
              'px-4 py-1.5 text-sm font-medium transition-colors flex items-center gap-1.5',
              tab === 'analysis' ? 'bg-tf-text text-white' : 'text-tf-text-muted hover:bg-tf-bg-hover',
              isAnalyzing && 'opacity-70',
            )}
          >
            {isAnalyzing ? (
              <Icons.Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Icons.Sparkles className="w-3.5 h-3.5" />
            )}
            AI Analysis
          </button>
        </div>
        <span className="text-sm text-tf-text-muted">{ads.length} ads</span>
      </div>

      {/* Content */}
      {tab === 'ads' ? (
        <div className="grid grid-cols-2 gap-3">
          {ads.map((ad) => (
            <AdCard key={ad.ad_id} ad={ad} />
          ))}
          {ads.length === 0 && (
            <div className="col-span-2 py-12 text-center text-sm text-tf-text-muted">
              No individual ads found
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-tf-border bg-tf-bg-card overflow-hidden">
          {/* Analysis header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-tf-border-sub bg-tf-bg-muted/50">
            <div className="flex items-center gap-2 text-sm font-medium text-tf-text-sub">
              <Icons.Sparkles className="w-4 h-4 text-primary" />
              Creative Performance Analysis
              {analysisResult && fromCache && (
                <span className="flex items-center gap-1 text-xs text-tf-text-muted font-normal ml-2">
                  <Icons.Database className="w-3 h-3" />
                  cached
                </span>
              )}
            </div>
            {analysisResult && (
              <button
                onClick={() => handleAnalyze(true)}
                disabled={isAnalyzing}
                className="flex items-center gap-1.5 text-xs text-primary hover:text-primary-dark transition-colors"
              >
                <Icons.RefreshCw className={cn('w-3 h-3', isAnalyzing && 'animate-spin')} />
                Regenerate
              </button>
            )}
          </div>

          {/* Analysis content */}
          <div className="p-5 max-h-[600px] overflow-y-auto">
            {isAnalyzing ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <Icons.Loader2 className="w-8 h-8 text-primary animate-spin" />
                <p className="text-sm font-medium text-tf-text-muted">Analyzing {ads.length} ads...</p>
                <p className="text-xs text-tf-text-muted">This may take 15-30 seconds</p>
              </div>
            ) : analysisError ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <Icons.AlertCircle className="w-8 h-8 text-tf-feedback-error-text" />
                <p className="text-sm font-medium text-tf-feedback-error-text">Analysis failed</p>
                <p className="text-xs text-tf-text-muted">{analysisError}</p>
                <button
                  onClick={() => handleAnalyze(true)}
                  className="mt-2 px-4 py-1.5 text-xs font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors"
                >
                  Try again
                </button>
              </div>
            ) : analysisResult ? (
              <div className="prose prose-sm prose-gray max-w-none">
                <ReactMarkdown>{analysisResult}</ReactMarkdown>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <Icons.Sparkles className="w-8 h-8 text-tf-border" />
                <p className="text-sm font-medium text-tf-text-muted">Click "AI Analysis" to generate insights</p>
                <p className="text-xs text-tf-text-muted">
                  Analyzes performance data and creative thumbnails using AI
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Main Panel
// ============================================================================

type PanelTab = 'group' | 'individual'

interface CreativeDetailPanelProps {
  data: CreativeDetailData
  onClose: () => void
}

export function CreativeDetailPanel({ data, onClose }: CreativeDetailPanelProps) {
  const [activeTab, setActiveTab] = useState<PanelTab>('group')
  const m = data.metrics

  // Close on Escape key
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose],
  )

  return (
    <div
      className="fixed top-0 left-0 right-0 bottom-0 z-[9999] bg-tf-bg-muted flex flex-col"
      style={{ margin: 0, padding: 0 }}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      ref={(el) => el?.focus()}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 bg-tf-bg-card border-b border-tf-border flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-tf-bg-hover transition-colors mr-1"
            title="Back to creatives"
          >
            <Icons.ArrowLeft className="w-5 h-5 text-tf-text-muted" />
          </button>
          <div className="flex items-center gap-2">
            <Icons.Layers className="w-5 h-5 text-tf-text-muted" />
            <span className="text-sm text-tf-text-muted">Ad Set:</span>
            <span className="text-sm font-semibold text-primary">
              {data.ad_group_name}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-tf-text-muted bg-tf-bg-hover px-3 py-1.5 rounded-lg flex items-center gap-1.5">
            <Icons.Calendar className="w-3.5 h-3.5" />
            {data.start_date} - {data.end_date}
          </span>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-tf-bg-hover transition-colors"
          >
            <Icons.X className="w-5 h-5 text-tf-text-muted" />
          </button>
        </div>
      </div>

      {/* Tabs + Metrics row */}
      <div className="bg-tf-bg-card border-b border-tf-border flex-shrink-0">
        <div className="flex items-center justify-between px-6">
          {/* Tabs */}
          <div className="flex gap-0 pt-1">
            <button
              onClick={() => setActiveTab('group')}
              className={cn(
                'px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'group'
                  ? 'border-tf-text text-tf-text'
                  : 'border-transparent text-tf-text-muted hover:text-tf-text-muted',
              )}
            >
            Ad Set
          </button>
          <button
            onClick={() => setActiveTab('individual')}
            className={cn(
              'px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'individual'
                ? 'border-tf-text text-tf-text'
                : 'border-transparent text-tf-text-muted hover:text-tf-text-muted',
            )}
          >
            Individual Ads
            </button>
          </div>

          {/* Metric cards inline */}
          <div className="flex items-center gap-10">
            <MetricCard label="ROAS" value={(m?.roas || 0).toFixed(2)} />
            <MetricCard label="Spend" value={formatCurrency(m?.spend || 0)} />
            <MetricCard label="CPC" value={`$${(m?.cpc || 0).toFixed(2)}`} />
            <MetricCard label="CVR" value={`${(m?.cvr || 0).toFixed(1)}%`} />
            <MetricCard label="CTR" value={`${(m?.ctr || 0).toFixed(1)}%`} />
            <MetricCard label="CPM" value={`$${(m?.cpm || 0).toFixed(2)}`} />
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'group' ? (
          <div className="flex gap-6 h-full">
            {/* Left: Charts */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-4">
                <Icons.BarChart3 className="w-4 h-4 text-tf-text-muted" />
                <h3 className="text-sm font-semibold text-tf-text">Charts</h3>
              </div>
              <WeeklyCharts data={data.weekly_metrics} />
            </div>

            {/* Right: Ads sidebar */}
            <div className="w-[380px] flex-shrink-0 overflow-y-auto">
              <AdsGrid ads={data.ads} detailData={data} />
            </div>
          </div>
        ) : (
          /* Individual Ads tab - full width grid */
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Icons.Image className="w-4 h-4 text-tf-text-muted" />
                  <h3 className="text-sm font-semibold text-tf-text">
                    All Ads in this Ad Set
                </h3>
              </div>
              <span className="text-sm text-tf-text-muted">{data.ads.length} ads</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {data.ads.map((ad) => (
                <AdCard key={ad.ad_id} ad={ad} />
              ))}
              {data.ads.length === 0 && (
                <div className="col-span-full py-16 text-center text-sm text-tf-text-muted">
                  No individual ads found for this ad group
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CreativeDetailPanel
